import { useCallback, useEffect, useState } from "react"
import { IconDatabaseExclamation, IconLoader2, IconRefresh, IconRefreshDot } from "@tabler/icons-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { api } from "@/lib/api"
import { APPOINTMENTS_ADMIN_PERMISSION } from "@/lib/appointments-access"
import { useAppSelector } from "@/store"
import { hasPermission } from "@/store/permissionsSlice"
import {
  RESULT,
  actionLabel,
  plannedCount,
  plannedSteps,
  resultLabel,
  summarize,
  type ReconciliationReport,
} from "./reconcile"

// ── Types ────────────────────────────────────────────────────────────
// Mirrors BookingDataGap / BookingDataGapReport in Cms.Services. `kind` is BookingDataGapKind,
// serialized as a number like every enum this API returns.

interface BookingDataGap {
  calBookingUid: string | null
  kind: number
  calValue: string | null
  cmsValue: string | null
  start: string
  end: string
  calStatus: string | null
  siteName: string | null
  hostName: string | null
  attendeeName: string | null
  requestId: string | null
  referenceCode: string | null
}

interface BookingDataGapReport {
  gaps: BookingDataGap[]
  calBookingCount: number
  upcomingCmsBookingCount: number
  truncated: boolean
  windowStart: string
  windowEnd: string
}

/** BookingDataGapKind — what is missing, and which side is short of it. */
const GAP_KINDS: Record<number, { label: string; description: string; variant: "destructive" | "secondary" }> = {
  0: { label: "Not in CMS", description: "The scheduling system has this booking; CMS has no record of it, so the calendar does not show it.", variant: "destructive" },
  1: { label: "Host", description: "CMS has no host, or a different one from the scheduling system's.", variant: "secondary" },
  2: { label: "Start time", description: "CMS has a different start from the scheduling system's.", variant: "secondary" },
  3: { label: "End time", description: "CMS has no end, or a different one from the scheduling system's.", variant: "secondary" },
  4: { label: "Status", description: "One side has the appointment live and the other has it cancelled.", variant: "destructive" },
  5: { label: "Attendee", description: "CMS has no attendee name where the scheduling system has one.", variant: "secondary" },
  6: { label: "No scheduling link", description: "An upcoming CMS booking with no scheduling-system booking — nothing is holding its slot.", variant: "destructive" },
  7: { label: "Scheduling booking missing", description: "An upcoming CMS booking whose booking the scheduling system does not have.", variant: "destructive" },
  8: { label: "Invalid", description: "Not a real booking: the slot belongs to someone else in the scheduling system. The household needs contacting.", variant: "destructive" },
  9: { label: "Slot conflict", description: "Another live CMS booking holds the same host's slot. Only one may.", variant: "destructive" },
  10: { label: "System error", description: "CMS refused this booking after the scheduling system made it (a double booking, or nothing in CMS stood behind it). Its scheduling-system booking is cancelled and the household was told not to attend; they may need rebooking.", variant: "destructive" },
}

/** The gaps the scheduling-link pass can resolve: an upcoming booking with no live scheduling-system booking behind it. */
const RESOLVABLE_KINDS = new Set([6, 7])

/** Mirrors CalLinkResolution. `kind` is CalLinkResolutionKind. */
interface CalLinkResolution {
  requestId: string
  kind: number
  message: string
  calBookingUid: string | null
}

function formatDateTime(value: string | null): string {
  if (!value) return "—"
  return new Date(value).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  })
}

/** A value from either side, which may be a timestamp. */
function formatValue(value: string | null): string {
  if (!value) return "—"
  return /^\d{4}-\d{2}-\d{2}T/.test(value) ? formatDateTime(value) : value
}

// ── Component ────────────────────────────────────────────────────────

export default function DataGapsPage() {
  const { permissions } = useAppSelector((state) => state.permissions)
  // A support tool, not part of working bookings: only a direct appointments:admin grant opens it.
  const canAdminister = hasPermission(permissions, APPOINTMENTS_ADMIN_PERMISSION)

  const [report, setReport] = useState<BookingDataGapReport | null>(null)
  const [loading, setLoading] = useState(true)

  // Inline, like Unassigned Hosts: an empty table beside a faded toast would read as "nothing missing".
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setReport(await api.get<BookingDataGapReport>("/api/ams/bookings/data-gaps"))
    } catch (err) {
      console.error("Failed to compare scheduling-system and CMS bookings:", err)
      setError(err instanceof Error ? err.message : "Failed to compare scheduling-system and CMS bookings.")
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (canAdminister) void load()
  }, [canAdminister, load])

  const rows = report?.gaps ?? []

  // What the last "Resolve" did, per booking request, so the outcome stays readable after the reload.
  const [resolving, setResolving] = useState<string | null>(null)
  const [outcomes, setOutcomes] = useState<Record<string, string>>({})

  const resolve = useCallback(async (requestId: string) => {
    setResolving(requestId)
    try {
      const result = await api.post<CalLinkResolution>(`/api/ams/booking-requests/${requestId}/cal-link`)
      setOutcomes(prev => ({ ...prev, [requestId]: result.message }))
      await load()
    } catch (err) {
      setOutcomes(prev => ({ ...prev, [requestId]: err instanceof Error ? err.message : "Could not resolve this booking." }))
    } finally {
      setResolving(null)
    }
  }, [load])

  const lastOutcomes = Object.entries(outcomes)

  // "Reconcile now" previews first (a dry run), and only "Apply" changes anything. Apply sends back
  // the preview's actions and the server takes only those, re-reading each booking before acting: it
  // may do less than the preview, never more. Anything new since is reported as skipped.
  const [reconciling, setReconciling] = useState<"preview" | "apply" | null>(null)
  const [reconcileReport, setReconcileReport] = useState<ReconciliationReport | null>(null)
  const [reconcileError, setReconcileError] = useState<string | null>(null)

  const reconcile = useCallback(async (preview: ReconciliationReport | null) => {
    const dryRun = preview === null
    setReconciling(dryRun ? "preview" : "apply")
    setReconcileError(null)
    try {
      const result = dryRun
        ? await api.post<ReconciliationReport>("/api/ams/bookings/reconcile?dryRun=true")
        : await api.post<ReconciliationReport>("/api/ams/bookings/reconcile/apply", { actions: plannedSteps(preview) })
      setReconcileReport(result)
      if (!dryRun) await load()
    } catch (err) {
      setReconcileError(err instanceof Error ? err.message : "Could not run the reconciler.")
    } finally {
      setReconciling(null)
    }
  }, [load])

  if (!canAdminister) {
    return (
      <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
        <div className="flex items-center gap-2">
          <IconDatabaseExclamation className="size-6" />
          <h1 className="text-2xl font-bold">Data Gaps</h1>
        </div>
        <p className="text-muted-foreground">You do not have permission to view data gaps.</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <IconDatabaseExclamation className="size-6" />
          <h1 className="text-2xl font-bold">Data Gaps</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void reconcile(null)} disabled={reconciling !== null}>
            {reconciling === "preview" ? (
              <IconLoader2 className="size-4 animate-spin" />
            ) : (
              <IconRefreshDot className="size-4" />
            )}
            Reconcile now
          </Button>
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            {loading ? (
              <IconLoader2 className="size-4 animate-spin" />
            ) : (
              <IconRefresh className="size-4" />
            )}
            Refresh
          </Button>
        </div>
      </div>
      <p className="text-muted-foreground max-w-3xl">
        The calendar shows bookings from CMS. The scheduling system handles the scheduling and reports
        back to CMS; this lists what the scheduling system knows that CMS was never told, and upcoming
        CMS bookings with no live scheduling-system booking holding their slot. Past bookings are not
        checked for their scheduling link — CMS's record is the history. The hourly maintenance sweep
        resolves missing scheduling links on its own: it links a matching scheduling-system booking,
        creates one if the host is free (the scheduling system then emails the attendee), or marks the
        booking Invalid if the slot belongs to someone else. "Resolve now" runs that for one booking.
      </p>
      <p className="text-muted-foreground max-w-3xl">
        The reconciler runs every few minutes and repairs what does not agree: it cancels a CMS booking the
        scheduling system has cancelled, cancels a scheduling-system booking CMS has closed, adopts a
        scheduling-system booking CMS lost the bookkeeping for, and marks a scheduling-system booking CMS
        cannot trace — or the later of two bookings on one host's time —
        a system error, cancelling it and telling the household not to attend. "Reconcile now" shows what
        it would do; nothing changes until you apply it.
      </p>

      {(reconcileReport || reconcileError) && (
        <div className="rounded-xl border bg-card p-4 text-sm flex flex-col gap-3">
          {reconcileError ? (
            <p className="text-destructive">{reconcileError}</p>
          ) : reconcileReport && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="font-medium">
                    {reconcileReport.dryRun ? "Reconciliation preview" : "Reconciliation applied"}
                  </div>
                  <div className="text-muted-foreground">{summarize(reconcileReport)}</div>
                </div>
                <div className="flex items-center gap-2">
                  {reconcileReport.dryRun && plannedCount(reconcileReport) > 0 && (
                    <Button size="sm" variant="destructive" onClick={() => void reconcile(reconcileReport)} disabled={reconciling !== null}>
                      {reconciling === "apply" && <IconLoader2 className="size-4 animate-spin" />}
                      Apply {plannedCount(reconcileReport)} {plannedCount(reconcileReport) === 1 ? "change" : "changes"}
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => setReconcileReport(null)} disabled={reconciling !== null}>
                    Dismiss
                  </Button>
                </div>
              </div>
              {reconcileReport.items.length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Appointment</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Why</TableHead>
                      <TableHead>Identifiers</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reconcileReport.items.map((item, index) => (
                      <TableRow key={`${item.calBookingUid ?? item.requestId}:${item.action}:${index}`}>
                        <TableCell>{formatDateTime(item.start)}</TableCell>
                        <TableCell>
                          <Badge variant={item.result === RESULT.failed || item.result === RESULT.reported ? "destructive" : "secondary"}>
                            {actionLabel(item.action)}
                          </Badge>
                          <div className="text-xs text-muted-foreground mt-1">{resultLabel(item.result)}</div>
                        </TableCell>
                        <TableCell className="max-w-md whitespace-normal">{item.message}</TableCell>
                        <TableCell>
                          <div className="text-sm">{item.referenceCode ?? "No CMS row"}</div>
                          <div className="text-xs text-muted-foreground font-mono break-all">{item.calBookingUid ?? "No scheduling-system ID"}</div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </div>
      )}

      {lastOutcomes.length > 0 && (
        <div className="rounded-xl border bg-card p-4 text-sm flex flex-col gap-1">
          {lastOutcomes.map(([id, message]) => (
            <div key={id}>{message}</div>
          ))}
        </div>
      )}

      {report?.truncated && (
        <div className="rounded-xl border bg-card p-4 text-sm">
          Only the first 250 scheduling-system bookings were compared, so this list may be short.
        </div>
      )}

      {loading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : error ? (
        <div className="flex flex-col items-start gap-2 rounded-xl border bg-card p-6">
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="outline" size="sm" onClick={() => void load()}>
            Retry
          </Button>
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border bg-card p-6">
          <div className="text-muted-foreground">CMS holds everything the scheduling system does, and every upcoming booking has its scheduling-system booking.</div>
          {report && (
            <div className="text-sm text-muted-foreground mt-1">
              Compared {report.calBookingCount} scheduling-system {report.calBookingCount === 1 ? "booking" : "bookings"} and{" "}
              {report.upcomingCmsBookingCount} upcoming CMS {report.upcomingCmsBookingCount === 1 ? "booking" : "bookings"} between{" "}
              {formatDateTime(report.windowStart)} and {formatDateTime(report.windowEnd)}.
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-xl border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Appointment</TableHead>
                <TableHead>Gap</TableHead>
                <TableHead>Scheduling system has</TableHead>
                <TableHead>CMS has</TableHead>
                <TableHead>Identifiers</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const kind = GAP_KINDS[row.kind] ?? { label: `Kind ${row.kind}`, description: "", variant: "secondary" as const }
                return (
                  // One booking can have several gaps, and a missing link has no uid.
                  <TableRow key={`${row.calBookingUid ?? row.requestId}:${row.kind}`}>
                    <TableCell>
                      <div className="font-medium">{formatDateTime(row.start)}</div>
                      <div className="text-sm text-muted-foreground">
                        {row.siteName ?? "—"} · {row.attendeeName ?? "—"}
                        {row.hostName ? ` · ${row.hostName}` : ""}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={kind.variant}>{kind.label}</Badge>
                      <div className="text-sm text-muted-foreground mt-1 max-w-xs">{kind.description}</div>
                    </TableCell>
                    <TableCell>{formatValue(row.calValue)}</TableCell>
                    <TableCell>{formatValue(row.cmsValue)}</TableCell>
                    <TableCell>
                      <div className="text-sm">{row.referenceCode ?? "No CMS row"}</div>
                      <div className="text-xs text-muted-foreground font-mono break-all">{row.calBookingUid ?? "No scheduling-system ID"}</div>
                      {row.requestId && RESOLVABLE_KINDS.has(row.kind) && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-2"
                          disabled={resolving !== null}
                          onClick={() => void resolve(row.requestId!)}
                        >
                          {resolving === row.requestId && <IconLoader2 className="size-4 animate-spin" />}
                          Resolve now
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}
